import { useEffect, useState } from 'react';
import { worklogApi } from '../api/worklogApi';
import { ThumbImg } from '../components/ThumbImg';
import { WorkOrderNoticeView } from '../components/WorkOrderNoticeView';
import { CustReqNoticeSection } from '../components/custReq/CustReqNoticeSection';
import { pickNoticeItems } from '../components/custReq/custReqNotice.logic';
import type { WorkOrderNotice, WorkOrderNoticePhoto } from '../types/worklog.types';
import type { CustReqNoticeFields } from '../types/custReqNotice.types';

/**
 * 반장 작업요청 안내 (외부·무인증 공유 화면) — 기획화면 `z-작업요청상세보기`.
 *
 * 반장(수신자)이 알림톡/문자의 공유링크로 여는 공개 뷰. RequireAuth·AppFrame 바깥
 * (F-04 §7)에서 `/share/WORK_ORDER_RECIPIENT?shareId=...` 로 진입한다. 자격증명은
 * pf-sharegate 발급 공유 쿠키(shareScope)이며, 조회 API는 토큰 미부착(auth-exempt, §2.14).
 * 작업요청은 발송 당시 값으로 불변이라 화면은 읽기 전용이다.
 *
 * 진입 전 만료/회수/인증실패는 공통 `/share-error` 가 받으므로(본 API는 GUEST JWT 취득
 * 후 호출), 여기서는 주로 세션 만료(410)만 방어한다.
 */

/** 'YYYY-MM-DD' → 'YYYY.MM.DD' */
function formatExpire(iso: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso.replace(/-/g, '.') : iso;
}

export function WorkOrderNoticePage() {
  const [state, setState] = useState<'loading' | 'error' | 'ready'>('loading');
  const [data, setData] = useState<WorkOrderNotice | null>(null);
  const [lbIndex, setLbIndex] = useState<number | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    worklogApi
      .getWorkOrderNoticeShare(controller.signal)
      .then((res) => {
        setData(res.data.data);
        setState('ready');
      })
      .catch((err) => {
        if (controller.signal.aborted || err?.code === 'ERR_CANCELED') return;
        setState('error');
      });
    return () => controller.abort();
  }, []);

  // ── 라이트박스 키보드 조작 ──
  useEffect(() => {
    if (lbIndex === null || !data) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLbIndex(null);
      else if (e.key === 'ArrowLeft') setLbIndex((i) => (i === null ? i : (i - 1 + data.photos.length) % data.photos.length));
      else if (e.key === 'ArrowRight') setLbIndex((i) => (i === null ? i : (i + 1) % data.photos.length));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lbIndex, data]);

  if (state === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-text-muted">
        불러오는 중…
      </div>
    );
  }

  if (state === 'error' || !data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-2 p-8 text-center">
        <h1 className="text-base font-bold text-text-primary">작업요청을 불러올 수 없습니다</h1>
        <p className="text-sm text-text-secondary">공유 링크가 만료되었거나 더 이상 유효하지 않습니다.</p>
      </div>
    );
  }

  // 버튼 활성/비활성은 canWriteReport를 쓴다. 2026-10-28 변경: 참조인(CC)도 작업자와 같이 작성할 수 있어 기한만 본다.
  const writable = data.canWriteReport;

  /**
   * 현장보고 작성(§3.9) 화면으로 이동. 링크를 추가 발급하지 않고 이미 보유한 공유 쿠키를
   * 그대로 쓴다 — SHARE_SCOPE=RESPOND 가 조회와 제출을 겸한다.
   * shareId 쿼리는 서버가 쓰지 않지만(주체는 쿠키), 진입 URL 형태를 유지하려 그대로 넘긴다.
   */
  const handleWriteReport = () => {
    window.location.assign(`/share/WORK_ORDER_RECIPIENT/field-report${window.location.search}`);
  };

  return (
    // data-group="02" = 인디공사현황 그룹 액센트(--gt-*) 적용 (D-08 §6.4)
    <div data-group="02" className="min-h-screen" style={{ background: 'var(--shell-bg)' }}>
      <div className="mx-auto flex min-h-screen w-full max-w-[480px] flex-col" style={{ background: 'var(--shell-bg)' }}>

        <WorkOrderNoticeView
          data={data}
          photos={data.photos}
          // 게스트 사진 — ThumbImg(apiClient blob)로 로드. 로컬(dev)은 주소창 shareId를
          // 쿼리로 실어 SHARE_NOT_FOUND 회피, 운영은 공유쿠키 자동 전송(withCredentials).
          renderThumb={(p: WorkOrderNoticePhoto, i) => <ThumbImg url={p.thumbUrl} alt={`참고사진 ${i + 1}`} className="h-full w-full" />}
          onPhotoClick={setLbIndex}
          hideEmptyOrderContent
          afterPhotos={<CustReqNoticeSection items={pickNoticeItems(data as WorkOrderNotice & CustReqNoticeFields)} showGuide={writable} />}
        />

        {/* ─ 현장보고 작성 CTA ─ */}
        <section className="mt-2" style={{ background: 'var(--shell-surface)', padding: '18px 16px 26px' }}>
          <p
            className="mb-3 text-center text-body-sm"
            style={{ color: writable ? 'var(--shell-hint)' : 'var(--color-error)', fontWeight: writable ? 400 : 600, letterSpacing: '-.2px' }}
          >
            {writable
              ? `현장보고 작성하기는 ${formatExpire(data.fieldReportExpireAt)} 까지 이용가능합니다.`
              : `공유 기간이 종료되었습니다 (${formatExpire(data.fieldReportExpireAt)} 만료).`}
          </p>
          <button
            type="button"
            onClick={handleWriteReport}
            disabled={!writable}
            className="flex w-full items-center justify-center gap-2 text-[15px] font-bold"
            style={{
              height: 52,
              border: 'none',
              borderRadius: 'var(--radius-md)',
              background: writable ? 'var(--gt-primary)' : 'var(--shell-skeleton)',
              color: writable ? 'var(--gt-on-primary)' : 'var(--shell-hint)',
              letterSpacing: '-.2px',
              cursor: writable ? 'pointer' : 'default',
              fontFamily: 'inherit',
            }}
          >
            {writable ? (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 20h9" />
                  <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
                </svg>
                현장보고 작성하기
              </>
            ) : (
              '현장보고 작성 기간 종료'
            )}
          </button>
          <p className="mt-2.5 text-center text-[length:var(--type-caption-size)]" style={{ color: 'var(--shell-hint)' }}>
            {writable
              ? '작업 후 현장 사진과 이슈를 기록할 수 있어요'
              : '작성 기간이 지나 더 이상 현장보고를 등록할 수 없습니다.'}
          </p>
        </section>
      </div>

      {/* ─ 라이트박스 (사진 확대) ─ */}
      {lbIndex !== null && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="사진 보기"
          className="fixed inset-0 z-modal flex flex-col"
          style={{ background: 'var(--alpha-black-900)' }}
          onClick={() => setLbIndex(null)}
        >
          <div className="flex items-center justify-between" style={{ padding: '14px 16px' }}>
            <span className="text-[13px] font-semibold" style={{ color: 'var(--alpha-white-700)' }}>
              {lbIndex + 1} / {data.photos.length}
            </span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setLbIndex(null); }}
              className="flex items-center justify-center text-title-md"
              style={{ width: 36, height: 36, borderRadius: 'var(--radius-full)', background: 'var(--alpha-white-100)', border: 'none', color: 'var(--gray-0)', cursor: 'pointer' }}
              aria-label="닫기"
            >
              ×
            </button>
          </div>
          <div className="relative flex flex-1 items-center justify-center overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {data.photos.length > 1 && (
              <button
                type="button"
                onClick={() => setLbIndex((i) => (i === null ? i : (i - 1 + data.photos.length) % data.photos.length))}
                className="absolute flex items-center justify-center text-[22px]"
                style={{ left: 12, top: '50%', transform: 'translateY(-50%)', width: 40, height: 40, borderRadius: 'var(--radius-full)', background: 'var(--alpha-white-100)', border: 'none', color: 'var(--gray-0)', cursor: 'pointer' }}
                aria-label="이전"
              >
                ‹
              </button>
            )}
            <ThumbImg
              url={data.photos[lbIndex].url}
              alt={`참고사진 ${lbIndex + 1}`}
              imgStyle={{ maxWidth: '92vw', maxHeight: '76vh', objectFit: 'contain', borderRadius: 'var(--radius-md)' }}
            />
            {data.photos.length > 1 && (
              <button
                type="button"
                onClick={() => setLbIndex((i) => (i === null ? i : (i + 1) % data.photos.length))}
                className="absolute flex items-center justify-center text-[22px]"
                style={{ right: 12, top: '50%', transform: 'translateY(-50%)', width: 40, height: 40, borderRadius: 'var(--radius-full)', background: 'var(--alpha-white-100)', border: 'none', color: 'var(--gray-0)', cursor: 'pointer' }}
                aria-label="다음"
              >
                ›
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
